import type {
  ComparabilityVerdict,
  ConfidenceBand,
  DuplicatePair,
  DuplicateReviewView,
  EstateApplicationRef,
  MetricStatus,
  MetricUnit,
  MetricValue,
  ModernizationCandidate,
  ModernizationDisposition,
  Money,
  PortfolioChangeKind,
  PortfolioChangeRow,
  PortfolioComparisonView,
  PortfolioMetricDelta,
  PortfolioSnapshotRef,
  QueryMetadata,
  QueryResult,
  TechnicalDebtBand,
  TechnologyHeatmapCell,
  TechnologyHeatmapView,
} from '@aurora-pvi/contracts';

/**
 * Estate fixtures — duplicate review, technology heatmap, portfolio comparison.
 *
 * These three projections are hand-authored rather than derived from the seeded
 * dataset, and that is a deliberate, temporary choice. All three depend on
 * inference the generator does not perform: a capability-overlap rule, a
 * lifecycle-and-debt model, and a period-to-period crosswalk. Deriving weak
 * versions of those from `buildAuroraDataset` would produce numbers that look
 * computed and are not, which is exactly the failure the screens themselves are
 * built to avoid. Written by hand, the figures are honest about their status:
 * `isSyntheticData` is true on every envelope and every screen prints it.
 *
 * Everything here is nevertheless kept consistent with the seeded estate's
 * scale — ~1,760 applications and ~142 products for Aurora Airlines — so that a
 * user moving between the inventory and these screens is not told two different
 * stories about how large the estate is.
 *
 * Populations always reconcile. Where a number is a subset of the estate, the
 * remainder is named with a reason rather than dropped:
 *
 *   duplicates   1,412 scanned + 291 + 57 unscanned  = 1,760
 *   heatmap      1,602 placed  + 112 +  46 unplaced  = 1,760
 *   comparison    122 unchanged + 4 + 5 + 4 + 2 + 1  =   138 baseline products
 *
 * When this moves behind a real detection service, only this file changes: the
 * screens bind to the contracts in `read-models.estate.ts` and cannot tell where
 * the rows came from.
 */

// ── Small deterministic helpers ──────────────────────────────────────────────

/**
 * A stable v4-shaped UUID from a readable seed.
 *
 * The contracts type these ids as UUIDs, and a fixture that used 'app-crewlink'
 * would type-check while failing the moment anyone ran the schema over it. This
 * keeps the ids valid and the fixture readable at the same time; the same seed
 * always yields the same id, so links and selections survive a reload.
 */
function uuidFrom(seed: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let index = 0; index < seed.length; index += 1) {
    h1 = Math.imul(h1 ^ seed.charCodeAt(index), 0x01000193) >>> 0;
    h2 = Math.imul(h2 + seed.charCodeAt(index) * (index + 7), 0x85ebca6b) >>> 0;
  }
  // `>>> 0` before formatting, because `^` and `+` yield *signed* 32-bit ints in
  // JavaScript: a negative result stringifies as "-5", which padStart turns into
  // "0000000-5" and injects a stray hyphen into the UUID body. That shifts every
  // later slice and produces ids like `…-a-e5-…` that fail `z.string().uuid()`.
  // It fires for roughly 0.3% of seeds, so it survives casual testing.
  const hex = (value: number) => (value >>> 0).toString(16).padStart(8, '0');
  const body = `${hex(h1)}${hex(h2)}${hex(h1 ^ h2)}${hex((h1 + h2) >>> 0)}`;
  return [
    body.slice(0, 8),
    body.slice(8, 12),
    `4${body.slice(13, 16)}`,
    `a${body.slice(17, 20)}`,
    body.slice(20, 32),
  ].join('-');
}

// USD, like every other money figure in the product. This fixture was the only
// thing in euros, so the heatmap's run cost sat beside a portfolio priced in
// dollars and read as a different, smaller number than it is.
const CURRENCY = 'USD';

function money(amount: number): Money {
  return { amount: amount.toFixed(2), currency: CURRENCY };
}

/** The dataset's fixed "today". Matching `@aurora-pvi/seed` keeps dates coherent. */
const AS_OF = '2026-05-20';
const REFRESHED_AT = '2026-05-20T02:15:00.000Z';

interface MetricOptions {
  status?: MetricStatus;
  unavailableReason?: string | null;
  desiredDirection?: MetricValue['desiredDirection'];
  confidence?: ConfidenceBand;
  qualityScore?: number | null;
  comparisonValue?: MetricValue['comparisonValue'];
  comparisonLabel?: string | null;
  periodStart?: string;
  periodEnd?: string;
  refreshedAt?: string | null;
  definitionVersion?: string;
  sourceMode?: MetricValue['sourceMode'];
  subjectId?: string | null;
  subjectType?: string | null;
}

/**
 * A governed metric value. Every figure on these screens goes through here, so
 * none of them can reach a tile without a definition version, a period, a
 * freshness stamp and a status — and so `Unavailable` always arrives with the
 * sentence that explains it rather than as a silent null.
 */
function metric(
  metricId: string,
  label: string,
  value: MetricValue['value'],
  unit: MetricUnit,
  options: MetricOptions = {},
): MetricValue {
  const status = options.status ?? (value === null ? 'Unavailable' : 'Available');
  return {
    metricId,
    label,
    subjectId: options.subjectId ?? null,
    subjectType: options.subjectType ?? null,
    value: status === 'Available' || status === 'Partial' ? value : null,
    unit,
    periodStart: options.periodStart ?? '2026-01-01',
    periodEnd: options.periodEnd ?? AS_OF,
    comparisonValue: options.comparisonValue ?? null,
    comparisonLabel: options.comparisonLabel ?? null,
    status,
    definitionVersion: options.definitionVersion ?? 'v4.0',
    sourceMode: options.sourceMode ?? 'Calculated',
    sourceRunId: null,
    refreshedAt: options.refreshedAt === undefined ? REFRESHED_AT : options.refreshedAt,
    qualityScore: options.qualityScore ?? 78,
    confidence: options.confidence ?? 'Medium',
    desiredDirection: options.desiredDirection ?? 'Context',
    sensitivity: 'Internal',
    allowedDrilldowns: [],
    unavailableReason: options.unavailableReason ?? null,
  };
}

/**
 * The response envelope.
 *
 * The three estate screens are read by architects and by finance, and the
 * difference between "the estate has 40 overlaps" and "the part of the estate
 * you can see has 40 overlaps" is the whole of `scope`. It is echoed on every
 * response rather than assumed.
 */
function estateMeta(options: {
  correlationId: string;
  sources: Array<{ name: string; status: MetricStatus; recordCount: number | null }>;
  qualityScore: number;
  openIssueCount: number;
  confidence: ConfidenceBand;
  allowedActions?: QueryMetadata['allowedActions'];
}): QueryMetadata {
  const overall: MetricStatus = options.sources.some((source) => source.status === 'Stale')
    ? 'Stale'
    : options.sources.some((source) => source.status === 'Partial')
      ? 'Partial'
      : 'Available';

  return {
    correlationId: options.correlationId,
    periodStart: '2026-01-01',
    periodEnd: AS_OF,
    comparisonPeriodStart: null,
    comparisonPeriodEnd: null,
    asOf: null,
    sourceStatus: {
      overall,
      refreshedAt: REFRESHED_AT,
      lastGoodAt: null,
      failedRunAt: null,
      contributingSources: options.sources.map((source) => ({
        sourceSystem: source.name,
        status: source.status,
        refreshedAt: REFRESHED_AT,
        recordCount: source.recordCount,
      })),
    },
    quality: {
      score: options.qualityScore,
      completeness: 0.86,
      validity: 0.93,
      uniqueness: 0.81,
      consistency: 0.88,
      timeliness: 0.9,
      openIssueCount: options.openIssueCount,
    },
    confidence: options.confidence,
    scope: {
      scopeId: uuidFrom('scope:enterprise'),
      scopeType: 'Enterprise',
      scopeLabel: 'Aurora Airlines — enterprise',
      isNarrowed: false,
      maskedFields: [],
    },
    allowedActions: options.allowedActions ?? [],
    isSyntheticData: true,
  };
}

// ── Applications referenced by more than one screen ──────────────────────────

interface AppSeed {
  code: string;
  name: string;
  lifecycle: EstateApplicationRef['lifecycle'];
  technicalHealth: EstateApplicationRef['technicalHealth'];
  criticality: EstateApplicationRef['criticality'];
  domain: string;
  vendor: string | null;
  annualRunCost: number | null;
  costUnavailableReason?: string;
}

function app(seed: AppSeed): EstateApplicationRef {
  return {
    id: uuidFrom(`app:${seed.code}`),
    businessCode: seed.code,
    name: seed.name,
    lifecycle: seed.lifecycle,
    technicalHealth: seed.technicalHealth,
    criticality: seed.criticality,
    domain: seed.domain,
    vendor: seed.vendor,
    annualRunCost: seed.annualRunCost === null ? null : money(seed.annualRunCost),
    costUnavailableReason:
      seed.annualRunCost === null
        ? (seed.costUnavailableReason ??
          'No cost centre is mapped to this application in the finance feed.')
        : null,
    route: `/applications/${uuidFrom(`app:${seed.code}`)}`,
  };
}

const APPS = {
  crewlink: app({
    code: 'APP-0142',
    name: 'CrewLink Rostering',
    lifecycle: 'Active',
    technicalHealth: 'Degraded',
    criticality: 'MissionCritical',
    domain: 'Flight Operations',
    vendor: 'Northwind Aviation Systems',
    annualRunCost: 1_840_000,
  }),
  aeroRoster: app({
    code: 'APP-0388',
    name: 'AeroRoster (Regional)',
    lifecycle: 'Active',
    technicalHealth: 'AtRisk',
    criticality: 'BusinessCritical',
    domain: 'Flight Operations',
    vendor: 'Northwind Aviation Systems',
    annualRunCost: 610_000,
  }),
  bagTrace: app({
    code: 'APP-0511',
    name: 'BagTrace',
    lifecycle: 'Active',
    technicalHealth: 'Good',
    criticality: 'BusinessCritical',
    domain: 'Ground Operations',
    vendor: 'Helios Ground Systems',
    annualRunCost: 720_000,
  }),
  bagRecon: app({
    code: 'APP-0907',
    name: 'Baggage Reconciliation Hub',
    lifecycle: 'Sunset',
    technicalHealth: 'Poor',
    criticality: 'BusinessCritical',
    domain: 'Ground Operations',
    vendor: null,
    annualRunCost: 455_000,
  }),
  skyMiles: app({
    code: 'APP-0033',
    name: 'SkyMiles Core',
    lifecycle: 'Active',
    technicalHealth: 'AtRisk',
    criticality: 'MissionCritical',
    domain: 'Loyalty & Retail',
    vendor: null,
    annualRunCost: 2_310_000,
  }),
  rewardsEngine: app({
    code: 'APP-1204',
    name: 'Aurora Rewards Engine',
    lifecycle: 'Active',
    technicalHealth: 'Good',
    criticality: 'BusinessCritical',
    domain: 'Loyalty & Retail',
    vendor: 'Meridian Loyalty Cloud',
    annualRunCost: 1_120_000,
  }),
  disruptionDesk: app({
    code: 'APP-0264',
    name: 'Disruption Desk',
    lifecycle: 'Active',
    technicalHealth: 'Degraded',
    criticality: 'MissionCritical',
    domain: 'Network Control',
    vendor: null,
    annualRunCost: null,
    costUnavailableReason:
      'Run cost sits in a shared Network Control cost centre and has not been split by application.',
  }),
  irregularOps: app({
    code: 'APP-0871',
    name: 'IrregOps Recovery Planner',
    lifecycle: 'Active',
    technicalHealth: 'AtRisk',
    criticality: 'BusinessCritical',
    domain: 'Network Control',
    vendor: 'Corvus Analytics',
    annualRunCost: 890_000,
  }),
  cargoBook: app({
    code: 'APP-0619',
    name: 'CargoBook',
    lifecycle: 'Active',
    technicalHealth: 'Good',
    criticality: 'Important',
    domain: 'Cargo',
    vendor: 'Helios Ground Systems',
    annualRunCost: 340_000,
  }),
  freightPortal: app({
    code: 'APP-1388',
    name: 'Freight Partner Portal',
    lifecycle: 'Active',
    technicalHealth: 'Degraded',
    criticality: 'Important',
    domain: 'Cargo',
    vendor: 'Helios Ground Systems',
    annualRunCost: 205_000,
  }),
  fuelOpt: app({
    code: 'APP-0455',
    name: 'FuelOpt',
    lifecycle: 'Active',
    technicalHealth: 'Good',
    criticality: 'BusinessCritical',
    domain: 'Flight Operations',
    vendor: 'Corvus Analytics',
    annualRunCost: 480_000,
  }),
  tankering: app({
    code: 'APP-1502',
    name: 'Tankering Advisor',
    lifecycle: 'Active',
    technicalHealth: 'Unknown',
    criticality: 'Important',
    domain: 'Flight Operations',
    vendor: null,
    annualRunCost: null,
    costUnavailableReason: 'Built in-house by the Fuel Efficiency team; no run cost is booked.',
  }),
  mroLegacy: app({
    code: 'APP-0078',
    name: 'MRO Workshop (AS/400)',
    lifecycle: 'Sunset',
    technicalHealth: 'Poor',
    criticality: 'BusinessCritical',
    domain: 'Engineering & Maintenance',
    vendor: 'Northwind Aviation Systems',
    annualRunCost: 1_260_000,
  }),
  partsLedger: app({
    code: 'APP-1044',
    name: 'Rotables Parts Ledger',
    lifecycle: 'Sunset',
    technicalHealth: 'Poor',
    criticality: 'Important',
    domain: 'Engineering & Maintenance',
    vendor: null,
    annualRunCost: 295_000,
  }),
  crewPay: app({
    code: 'APP-0290',
    name: 'Crew Pay Calculator',
    lifecycle: 'Superseded',
    technicalHealth: 'Poor',
    criticality: 'Important',
    domain: 'People',
    vendor: null,
    annualRunCost: 175_000,
  }),
  gateAssign: app({
    code: 'APP-0733',
    name: 'Gate Assignment Optimiser',
    lifecycle: 'Active',
    technicalHealth: 'Degraded',
    criticality: 'BusinessCritical',
    domain: 'Ground Operations',
    vendor: 'Helios Ground Systems',
    annualRunCost: 385_000,
  }),
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// 1. Duplicate review (UC-07-048)
// ─────────────────────────────────────────────────────────────────────────────

interface PairSeed {
  key: string;
  left: EstateApplicationRef;
  right: EstateApplicationRef;
  confidenceScore: number;
  confidence: ConfidenceBand;
  confidenceMethod: DuplicatePair['confidenceMethod'];
  sharedCapabilities: string[];
  processOverlap: number | null;
  processOverlapUnavailableReason?: string;
  evidence: DuplicatePair['evidence'];
  status?: DuplicatePair['status'];
  dismissal?: DuplicatePair['dismissal'];
  recordedDisposition?: DuplicatePair['recordedDisposition'];
  firstFlaggedAt: string;
}

/**
 * Combine two run costs.
 *
 * Returns `Partial` rather than a total when one side is unknown, because a
 * "combined cost" that silently omits a missing half is the most expensive kind
 * of wrong number on this screen: it is the figure a consolidation business case
 * is built from.
 */
function combineCost(pair: {
  left: EstateApplicationRef;
  right: EstateApplicationRef;
}): Pick<
  DuplicatePair,
  'combinedAnnualCost' | 'combinedCostStatus' | 'combinedCostUnavailableReason'
> {
  const left = pair.left.annualRunCost;
  const right = pair.right.annualRunCost;
  if (left && right) {
    return {
      combinedAnnualCost: money(Number(left.amount) + Number(right.amount)),
      combinedCostStatus: 'Available',
      combinedCostUnavailableReason: null,
    };
  }
  const known = left ?? right;
  const missing = left ? pair.right : pair.left;
  return {
    combinedAnnualCost: known ? money(Number(known.amount)) : null,
    combinedCostStatus: known ? 'Partial' : 'Unavailable',
    combinedCostUnavailableReason: known
      ? `Covers ${(left ? pair.left : pair.right).name} only. ${missing.costUnavailableReason ?? 'The other side carries no run cost.'}`
      : 'Neither application carries a run cost in the finance feed.',
  };
}

function pair(seed: PairSeed): DuplicatePair {
  return {
    id: uuidFrom(`pair:${seed.key}`),
    left: seed.left,
    right: seed.right,
    evidence: seed.evidence,
    confidenceScore: seed.confidenceScore,
    confidence: seed.confidence,
    confidenceMethod: seed.confidenceMethod,
    sharedCapabilities: seed.sharedCapabilities,
    processOverlap: seed.processOverlap,
    processOverlapUnavailableReason:
      seed.processOverlap === null
        ? (seed.processOverlapUnavailableReason ??
          'One of the two applications has no process mapping, so no overlap can be computed.')
        : null,
    ...combineCost(seed),
    status: seed.status ?? 'Open',
    dismissal: seed.dismissal ?? null,
    recordedDisposition: seed.recordedDisposition ?? null,
    firstFlaggedAt: seed.firstFlaggedAt,
  };
}

const PAIRS: DuplicatePair[] = [
  pair({
    key: 'crewlink-aeroroster',
    left: APPS.crewlink,
    right: APPS.aeroRoster,
    confidenceScore: 0.91,
    confidence: 'High',
    confidenceMethod: 'RuleDerivedDeterministic',
    sharedCapabilities: ['Crew Rostering (BC-114)', 'Crew Qualification Check (BC-117)'],
    processOverlap: 0.78,
    firstFlaggedAt: '2026-02-11T08:40:00.000Z',
    evidence: [
      {
        kind: 'SharedCapability',
        label: 'Both mapped to Crew Rostering (BC-114) as the primary application',
        detail: 'Two primaries against one capability; the operating model allows one.',
        weight: 0.42,
        sourceSystem: 'AURORA-CMDB',
        observedAt: '2026-05-19T22:10:00.000Z',
      },
      {
        kind: 'ProcessOverlap',
        label: 'Cover the same crew-planning processes',
        detail: '7 of the 9 L3 processes under Plan Crew are covered by both.',
        weight: 0.31,
        sourceSystem: 'Process Repository',
        observedAt: '2026-05-18T11:05:00.000Z',
      },
      {
        kind: 'VendorOverlap',
        label: 'Same vendor, two contracts',
        detail: 'Northwind Aviation Systems — contracts CT-2291 and CT-3140.',
        weight: 0.18,
        sourceSystem: 'Contract Register',
        observedAt: '2026-04-30T09:00:00.000Z',
      },
    ],
  }),
  pair({
    key: 'bagtrace-bagrecon',
    left: APPS.bagTrace,
    right: APPS.bagRecon,
    confidenceScore: 0.84,
    confidence: 'High',
    confidenceMethod: 'RuleDerivedDeterministic',
    sharedCapabilities: ['Baggage Tracking (BC-231)'],
    processOverlap: 0.64,
    recordedDisposition: 'Consolidate',
    status: 'UnderReview',
    firstFlaggedAt: '2025-11-04T14:22:00.000Z',
    evidence: [
      {
        kind: 'SharedCapability',
        label: 'Both mapped to Baggage Tracking (BC-231)',
        detail: 'BagTrace is primary; the Reconciliation Hub is supporting.',
        weight: 0.4,
        sourceSystem: 'AURORA-CMDB',
        observedAt: '2026-05-19T22:10:00.000Z',
      },
      {
        kind: 'SharedIntegration',
        label: 'Both consume the same BagMessage (BSM/BPM) feed',
        detail: 'Integration INT-0442, duplicated subscription since 2023.',
        weight: 0.26,
        sourceSystem: 'Integration Registry',
        observedAt: '2026-05-12T06:30:00.000Z',
      },
      {
        kind: 'ProcessOverlap',
        label: 'Overlap across mishandled-baggage handling',
        detail: '9 of 14 L3 processes under Handle Baggage.',
        weight: 0.18,
        sourceSystem: 'Process Repository',
        observedAt: '2026-05-18T11:05:00.000Z',
      },
    ],
  }),
  pair({
    key: 'skymiles-rewards',
    left: APPS.skyMiles,
    right: APPS.rewardsEngine,
    confidenceScore: 0.72,
    confidence: 'Medium',
    confidenceMethod: 'AssistedCandidate',
    sharedCapabilities: ['Loyalty Accrual (BC-302)'],
    processOverlap: 0.41,
    firstFlaggedAt: '2026-03-27T16:12:00.000Z',
    evidence: [
      {
        kind: 'SharedCapability',
        label: 'Both mapped to Loyalty Accrual (BC-302)',
        detail: 'Redemption (BC-303) sits only on SkyMiles Core.',
        weight: 0.34,
        sourceSystem: 'AURORA-CMDB',
        observedAt: '2026-05-19T22:10:00.000Z',
      },
      {
        kind: 'ProcessOverlap',
        label: 'Partial overlap on accrual and tier evaluation',
        detail: '5 of 12 L3 processes under Manage Loyalty.',
        weight: 0.22,
        sourceSystem: 'Process Repository',
        observedAt: '2026-05-18T11:05:00.000Z',
      },
      {
        kind: 'NameSimilarity',
        label: 'Names both describe a rewards engine',
        detail: 'Weakest of the three signals and weighted accordingly.',
        weight: 0.06,
        sourceSystem: null,
        observedAt: null,
      },
    ],
  }),
  pair({
    key: 'disruption-irregops',
    left: APPS.disruptionDesk,
    right: APPS.irregularOps,
    confidenceScore: 0.68,
    confidence: 'Medium',
    confidenceMethod: 'AssistedCandidate',
    sharedCapabilities: ['Disruption Recovery (BC-085)'],
    processOverlap: null,
    processOverlapUnavailableReason:
      'Disruption Desk has no process mapping, so no overlap percentage can be computed for this pair.',
    firstFlaggedAt: '2026-01-19T10:02:00.000Z',
    evidence: [
      {
        kind: 'SharedCapability',
        label: 'Both mapped to Disruption Recovery (BC-085)',
        detail: 'Both recorded as primary, in different regions.',
        weight: 0.38,
        sourceSystem: 'AURORA-CMDB',
        observedAt: '2026-05-19T22:10:00.000Z',
      },
      {
        kind: 'SharedIntegration',
        label: 'Both write recovery plans to the Ops Data Store',
        detail: 'Integration INT-0118, two writers on one topic.',
        weight: 0.3,
        sourceSystem: 'Integration Registry',
        observedAt: '2026-05-12T06:30:00.000Z',
      },
    ],
  }),
  pair({
    key: 'cargobook-freightportal',
    left: APPS.cargoBook,
    right: APPS.freightPortal,
    confidenceScore: 0.55,
    confidence: 'Medium',
    confidenceMethod: 'AssistedCandidate',
    sharedCapabilities: ['Cargo Booking (BC-410)'],
    processOverlap: 0.33,
    firstFlaggedAt: '2026-04-02T07:45:00.000Z',
    evidence: [
      {
        kind: 'SharedCapability',
        label: 'Both mapped to Cargo Booking (BC-410)',
        detail: 'The portal is a partner-facing front end over the same capability.',
        weight: 0.3,
        sourceSystem: 'AURORA-CMDB',
        observedAt: '2026-05-19T22:10:00.000Z',
      },
      {
        kind: 'VendorOverlap',
        label: 'Same vendor and licence pool',
        detail: 'Helios Ground Systems — one enterprise agreement, two SKUs.',
        weight: 0.15,
        sourceSystem: 'Contract Register',
        observedAt: '2026-04-30T09:00:00.000Z',
      },
      {
        kind: 'ProcessOverlap',
        label: 'Overlap on quote and booking capture',
        detail: '4 of 12 L3 processes under Sell Cargo Capacity.',
        weight: 0.1,
        sourceSystem: 'Process Repository',
        observedAt: '2026-05-18T11:05:00.000Z',
      },
    ],
  }),
  pair({
    key: 'fuelopt-tankering',
    left: APPS.fuelOpt,
    right: APPS.tankering,
    confidenceScore: 0.38,
    confidence: 'Low',
    confidenceMethod: 'AssistedCandidate',
    sharedCapabilities: [],
    processOverlap: null,
    processOverlapUnavailableReason:
      'Tankering Advisor is not mapped to any process, so there is nothing to compare against FuelOpt.',
    firstFlaggedAt: '2026-05-06T13:31:00.000Z',
    evidence: [
      {
        kind: 'NameSimilarity',
        label: 'Both names describe fuel optimisation',
        detail: 'This pair rests on the name alone — no shared capability was found.',
        weight: 0.2,
        sourceSystem: null,
        observedAt: null,
      },
      {
        kind: 'VendorOverlap',
        label: 'Adjacent teams, one vendor relationship',
        detail: 'FuelOpt is Corvus Analytics; Tankering Advisor is in-house.',
        weight: 0.18,
        sourceSystem: 'Contract Register',
        observedAt: '2026-04-30T09:00:00.000Z',
      },
    ],
  }),
  pair({
    key: 'mro-partsledger',
    left: APPS.mroLegacy,
    right: APPS.partsLedger,
    confidenceScore: 0.63,
    confidence: 'Medium',
    confidenceMethod: 'RuleDerivedDeterministic',
    sharedCapabilities: ['Rotable Parts Management (BC-523)'],
    processOverlap: 0.52,
    status: 'Dismissed',
    dismissal: {
      reason: 'ConsolidationAlreadyPlanned',
      note: 'Both retire into the new MRO platform at the FY27 Q1 cutover; PRG-118 tracks it.',
      actorName: 'S. Okonkwo',
      dismissedAt: '2026-04-14T09:20:00.000Z',
    },
    firstFlaggedAt: '2025-09-30T08:00:00.000Z',
    evidence: [
      {
        kind: 'SharedCapability',
        label: 'Both mapped to Rotable Parts Management (BC-523)',
        detail: 'The ledger holds the stock record; the workshop app holds the movements.',
        weight: 0.35,
        sourceSystem: 'AURORA-CMDB',
        observedAt: '2026-05-19T22:10:00.000Z',
      },
      {
        kind: 'ProcessOverlap',
        label: 'Overlap on parts issue and return',
        detail: '6 of 11 L3 processes under Maintain Aircraft.',
        weight: 0.28,
        sourceSystem: 'Process Repository',
        observedAt: '2026-05-18T11:05:00.000Z',
      },
    ],
  }),
  pair({
    key: 'gate-bagtrace',
    left: APPS.gateAssign,
    right: APPS.bagTrace,
    confidenceScore: 0.29,
    confidence: 'Low',
    confidenceMethod: 'AssistedCandidate',
    sharedCapabilities: [],
    processOverlap: 0.12,
    status: 'Dismissed',
    dismissal: {
      reason: 'EvidenceIncorrect',
      note: 'The shared integration is a published event both consume. That is integration, not overlap.',
      actorName: 'M. Lindqvist',
      dismissedAt: '2026-05-02T15:48:00.000Z',
    },
    firstFlaggedAt: '2026-04-28T06:15:00.000Z',
    evidence: [
      {
        kind: 'SharedIntegration',
        label: 'Both subscribe to the turnaround event stream',
        detail: 'Integration INT-0503.',
        weight: 0.22,
        sourceSystem: 'Integration Registry',
        observedAt: '2026-05-12T06:30:00.000Z',
      },
      {
        kind: 'ProcessOverlap',
        label: 'Slight overlap at the aircraft turnaround boundary',
        detail: '2 of 17 L3 processes under Turn Aircraft.',
        weight: 0.07,
        sourceSystem: 'Process Repository',
        observedAt: '2026-05-18T11:05:00.000Z',
      },
    ],
  }),
];

const DUPLICATE_VIEW: DuplicateReviewView = {
  kpis: [
    metric('MET-311', 'Open overlap claims', 34, 'count', {
      desiredDirection: 'Down',
      comparisonValue: 41,
      comparisonLabel: 'FY25 H2 close',
      confidence: 'High',
      qualityScore: 84,
    }),
    metric('MET-312', 'High-confidence claims', 9, 'count', {
      desiredDirection: 'Down',
      confidence: 'High',
      qualityScore: 84,
    }),
    metric('MET-313', 'Run cost inside open claims', money(18_640_000), 'currency', {
      status: 'Partial',
      desiredDirection: 'Down',
      confidence: 'Medium',
      qualityScore: 61,
      unavailableReason:
        'Covers the 27 open claims where both applications carry a run cost. Seven claims include an application with no cost booked and are excluded rather than counted as zero.',
    }),
    metric('MET-314', 'Claims dismissed in this period', 12, 'count', {
      desiredDirection: 'Context',
      confidence: 'High',
      qualityScore: 90,
    }),
  ],
  pairs: PAIRS,
  clusters: [
    {
      capabilityId: 'BC-114',
      capabilityName: 'Crew Rostering',
      applicationCount: 4,
      openPairCount: 3,
      intendedApplicationCount: 1,
      intendedCountUnavailableReason: null,
    },
    {
      capabilityId: 'BC-231',
      capabilityName: 'Baggage Tracking',
      applicationCount: 3,
      openPairCount: 2,
      intendedApplicationCount: 1,
      intendedCountUnavailableReason: null,
    },
    {
      capabilityId: 'BC-302',
      capabilityName: 'Loyalty Accrual',
      applicationCount: 3,
      openPairCount: 2,
      intendedApplicationCount: 2,
      intendedCountUnavailableReason: null,
    },
    {
      capabilityId: 'BC-085',
      capabilityName: 'Disruption Recovery',
      applicationCount: 5,
      openPairCount: 4,
      intendedApplicationCount: null,
      intendedCountUnavailableReason:
        'The operating model has not set a target count for this capability, so nothing here is over or under target.',
    },
    {
      capabilityId: 'BC-410',
      capabilityName: 'Cargo Booking',
      applicationCount: 3,
      openPairCount: 1,
      intendedApplicationCount: 2,
      intendedCountUnavailableReason: null,
    },
  ],
  coverage: {
    applicationsInScope: 1760,
    applicationsScanned: 1412,
    notScanned: [
      {
        applicationCount: 291,
        reason:
          'No business-capability mapping, so the capability and process rules had nothing to match on.',
      },
      {
        applicationCount: 57,
        reason: 'Retired before the scan window opened on 1 January 2026.',
      },
    ],
    lastScanAt: '2026-05-19T22:10:00.000Z',
    ruleSetVersion: 'overlap-rules v2.3',
  },
  dismissalOptions: [
    {
      value: 'DifferentBusinessScope',
      label: 'Different business scope',
      description:
        'They share a capability name but serve different markets, fleets or customer segments.',
    },
    {
      value: 'DeliberateRedundancy',
      label: 'Deliberate redundancy',
      description:
        'The second application exists on purpose — resilience, segregation or failover.',
    },
    {
      value: 'RegionalVariant',
      label: 'Regional variant',
      description: 'A local instance kept for a regulatory or language requirement.',
    },
    {
      value: 'ConsolidationAlreadyPlanned',
      label: 'Consolidation already planned',
      description: 'A programme already covers this; the claim adds nothing new.',
    },
    {
      value: 'EvidenceIncorrect',
      label: 'The evidence is wrong',
      description:
        'The mapping, integration or process data behind the claim is inaccurate. This one is routed back to the steward.',
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// 2. Technology heatmap (UC-07-049)
// ─────────────────────────────────────────────────────────────────────────────

const LIFECYCLE_AXIS = ['Active', 'Sunset', 'Superseded', 'Retired'] as const;
const DEBT_AXIS: TechnicalDebtBand[] = ['Low', 'Moderate', 'High', 'Severe'];

interface CellSeed {
  lifecycle: (typeof LIFECYCLE_AXIS)[number];
  debtBand: TechnicalDebtBand;
  disposition: ModernizationDisposition;
  applicationCount: number;
  criticalApplicationCount: number;
  annualRunCost: number | null;
  costStatus?: MetricStatus;
  costUnavailableReason?: string;
  dispositionRule: string;
  confidence: ConfidenceBand;
  sample?: EstateApplicationRef[];
}

function cell(seed: CellSeed): TechnologyHeatmapCell {
  return {
    id: `${seed.lifecycle}:${seed.debtBand}`,
    lifecycle: seed.lifecycle,
    debtBand: seed.debtBand,
    disposition: seed.disposition,
    applicationCount: seed.applicationCount,
    criticalApplicationCount: seed.criticalApplicationCount,
    annualRunCost: seed.annualRunCost === null ? null : money(seed.annualRunCost),
    costStatus: seed.costStatus ?? (seed.annualRunCost === null ? 'Unavailable' : 'Available'),
    costUnavailableReason: seed.costUnavailableReason ?? null,
    dispositionRule: seed.dispositionRule,
    confidence: seed.confidence,
    sampleApplications: seed.sample ?? [],
  };
}

/**
 * The grid. Counts sum to 1,602 placed applications; the remaining 158 are in
 * `unplaced` with the reason they could not be placed, never inside a cell.
 */
const CELLS: TechnologyHeatmapCell[] = [
  cell({
    lifecycle: 'Active',
    debtBand: 'Low',
    disposition: 'Invest',
    applicationCount: 388,
    criticalApplicationCount: 96,
    annualRunCost: 41_200_000,
    dispositionRule: 'Active lifecycle with low debt: the estate invests here rather than defends.',
    confidence: 'High',
    sample: [APPS.rewardsEngine, APPS.cargoBook],
  }),
  cell({
    lifecycle: 'Active',
    debtBand: 'Moderate',
    disposition: 'Tolerate',
    applicationCount: 402,
    criticalApplicationCount: 118,
    annualRunCost: 38_900_000,
    dispositionRule:
      'Active with moderate debt: carried as-is until debt crosses the High threshold or the vendor announces an end of support.',
    confidence: 'High',
    sample: [APPS.bagTrace, APPS.fuelOpt],
  }),
  cell({
    lifecycle: 'Active',
    debtBand: 'High',
    disposition: 'Migrate',
    applicationCount: 214,
    criticalApplicationCount: 74,
    annualRunCost: 29_400_000,
    dispositionRule:
      'Active but structurally indebted: still needed, so the route is re-platform rather than retire.',
    confidence: 'Medium',
    sample: [APPS.crewlink, APPS.gateAssign],
  }),
  cell({
    lifecycle: 'Active',
    debtBand: 'Severe',
    disposition: 'Migrate',
    applicationCount: 63,
    criticalApplicationCount: 31,
    annualRunCost: 12_100_000,
    dispositionRule:
      'Active with severe debt: migration is urgent, but 31 of these are mission- or business-critical and cannot simply be switched off.',
    confidence: 'Medium',
    sample: [APPS.skyMiles, APPS.aeroRoster],
  }),
  cell({
    lifecycle: 'Sunset',
    debtBand: 'Low',
    disposition: 'Tolerate',
    applicationCount: 41,
    criticalApplicationCount: 6,
    annualRunCost: 2_700_000,
    dispositionRule:
      'Already scheduled out and cheap to keep running: no separate modernization action is warranted.',
    confidence: 'High',
  }),
  cell({
    lifecycle: 'Sunset',
    debtBand: 'Moderate',
    disposition: 'Migrate',
    applicationCount: 96,
    criticalApplicationCount: 22,
    annualRunCost: 7_450_000,
    dispositionRule: 'Sunset with real debt: the successor migration is the modernization action.',
    confidence: 'Medium',
  }),
  cell({
    lifecycle: 'Sunset',
    debtBand: 'High',
    disposition: 'Migrate',
    applicationCount: 133,
    criticalApplicationCount: 38,
    annualRunCost: 14_800_000,
    dispositionRule:
      'Sunset with high debt: the cost of keeping these alive to the sunset date is itself a decision to make explicit.',
    confidence: 'Medium',
    sample: [APPS.mroLegacy],
  }),
  cell({
    lifecycle: 'Sunset',
    debtBand: 'Severe',
    disposition: 'Eliminate',
    applicationCount: 74,
    criticalApplicationCount: 19,
    annualRunCost: 9_300_000,
    dispositionRule:
      'Sunset and severely indebted: bring the decommission date forward rather than fund another year of support.',
    confidence: 'High',
    sample: [APPS.bagRecon, APPS.partsLedger],
  }),
  cell({
    lifecycle: 'Superseded',
    debtBand: 'Low',
    disposition: 'Eliminate',
    applicationCount: 12,
    criticalApplicationCount: 0,
    annualRunCost: 410_000,
    dispositionRule: 'A successor is already live. Low debt does not justify keeping both.',
    confidence: 'High',
  }),
  cell({
    lifecycle: 'Superseded',
    debtBand: 'Moderate',
    disposition: 'Eliminate',
    applicationCount: 27,
    criticalApplicationCount: 2,
    annualRunCost: 1_240_000,
    dispositionRule: 'A successor is already live; these are residual instances awaiting shutdown.',
    confidence: 'High',
    sample: [APPS.crewPay],
  }),
  cell({
    lifecycle: 'Superseded',
    debtBand: 'High',
    disposition: 'Eliminate',
    applicationCount: 44,
    criticalApplicationCount: 5,
    annualRunCost: 3_100_000,
    dispositionRule:
      'A successor is live and the residual debt is high: every month these stay up is avoidable cost and avoidable risk.',
    confidence: 'Medium',
  }),
  cell({
    lifecycle: 'Superseded',
    debtBand: 'Severe',
    disposition: 'Eliminate',
    applicationCount: 21,
    criticalApplicationCount: 3,
    annualRunCost: null,
    costStatus: 'Partial',
    costUnavailableReason:
      '14 of the 21 carry no cost centre since their successor went live. The cell is left without a figure rather than showing the partial total as if it were complete.',
    dispositionRule: 'Superseded and severely indebted: the shortest queue to decommission.',
    confidence: 'Medium',
  }),
  cell({
    lifecycle: 'Retired',
    debtBand: 'Low',
    disposition: 'Eliminate',
    applicationCount: 33,
    criticalApplicationCount: 0,
    annualRunCost: 180_000,
    dispositionRule:
      'Marked retired but still incurring cost: confirm the decommission actually completed.',
    confidence: 'High',
  }),
  cell({
    lifecycle: 'Retired',
    debtBand: 'Moderate',
    disposition: 'Eliminate',
    applicationCount: 24,
    criticalApplicationCount: 0,
    annualRunCost: 220_000,
    dispositionRule: 'Retired records with residual spend. Verify against the contract register.',
    confidence: 'Medium',
  }),
  cell({
    lifecycle: 'Retired',
    debtBand: 'High',
    disposition: 'Eliminate',
    applicationCount: 18,
    criticalApplicationCount: 1,
    annualRunCost: 340_000,
    dispositionRule:
      'Retired in the CMDB yet still generating debt findings — one of these is still flagged business-critical, which is a data conflict, not a plan.',
    confidence: 'Low',
  }),
  cell({
    lifecycle: 'Retired',
    debtBand: 'Severe',
    disposition: 'Eliminate',
    applicationCount: 12,
    criticalApplicationCount: 0,
    annualRunCost: 260_000,
    dispositionRule: 'Retired with severe findings: almost certainly stale records. Re-verify.',
    confidence: 'Low',
  }),
];

const QUEUE: ModernizationCandidate[] = [
  {
    application: APPS.skyMiles,
    disposition: 'Migrate',
    debtBand: 'Severe',
    debtScore: 88,
    debtScoreUnavailableReason: null,
    supportEndsOn: null,
    supportEndUnavailableReason: 'Built in-house; no vendor support date exists to record.',
    driver: 'Core loyalty ledger on an unsupported runtime, mission-critical, no failover.',
    confidence: 'High',
  },
  {
    application: APPS.mroLegacy,
    disposition: 'Migrate',
    debtBand: 'High',
    debtScore: 79,
    debtScoreUnavailableReason: null,
    supportEndsOn: '2027-03-31',
    supportEndUnavailableReason: null,
    driver: 'Vendor support ends in FY27 Q1; the AS/400 skills pool is three people.',
    confidence: 'High',
  },
  {
    application: APPS.bagRecon,
    disposition: 'Eliminate',
    debtBand: 'Severe',
    debtScore: 91,
    debtScoreUnavailableReason: null,
    supportEndsOn: '2026-09-30',
    supportEndUnavailableReason: null,
    driver: 'Sunset, severe debt, and a live overlap claim against BagTrace.',
    confidence: 'High',
  },
  {
    application: APPS.crewlink,
    disposition: 'Migrate',
    debtBand: 'High',
    debtScore: 74,
    debtScoreUnavailableReason: null,
    supportEndsOn: '2028-06-30',
    supportEndUnavailableReason: null,
    driver: 'Mission-critical, degraded, and one of four applications on Crew Rostering.',
    confidence: 'Medium',
  },
  {
    application: APPS.tankering,
    disposition: 'Tolerate',
    debtBand: 'Low',
    debtScore: null,
    debtScoreUnavailableReason:
      'No technical-debt assessment has been run: the application is not in the engineering scan scope.',
    supportEndsOn: null,
    supportEndUnavailableReason: 'In-house build; no vendor support contract.',
    driver: 'Placed on lifecycle alone. Without a debt score its position is provisional.',
    confidence: 'Low',
  },
  {
    application: APPS.partsLedger,
    disposition: 'Eliminate',
    debtBand: 'Severe',
    debtScore: 85,
    debtScoreUnavailableReason: null,
    supportEndsOn: null,
    supportEndUnavailableReason: 'In-house build; no vendor support contract.',
    driver: 'Sunset with the MRO programme; retire at the FY27 Q1 cutover.',
    confidence: 'High',
  },
  {
    application: APPS.aeroRoster,
    disposition: 'Migrate',
    debtBand: 'Severe',
    debtScore: 81,
    debtScoreUnavailableReason: null,
    supportEndsOn: '2026-12-31',
    supportEndUnavailableReason: null,
    driver: 'Vendor support ends this calendar year and CrewLink already covers the capability.',
    confidence: 'High',
  },
  {
    application: APPS.crewPay,
    disposition: 'Eliminate',
    debtBand: 'Moderate',
    debtScore: 58,
    debtScoreUnavailableReason: null,
    supportEndsOn: null,
    supportEndUnavailableReason: 'Superseded in FY25 H2; the contract was not renewed.',
    driver: 'Superseded by the payroll platform, still running for historical queries.',
    confidence: 'Medium',
  },
];

const HEATMAP_VIEW: TechnologyHeatmapView = {
  kpis: [
    metric('MET-321', 'Applications mapped', 1602, 'count', {
      desiredDirection: 'Up',
      confidence: 'High',
      qualityScore: 82,
      comparisonValue: 1544,
      comparisonLabel: 'FY25 H2 close',
    }),
    metric('MET-323', 'Run cost on Eliminate', money(15_050_000), 'currency', {
      status: 'Partial',
      desiredDirection: 'Down',
      confidence: 'Medium',
      qualityScore: 58,
      unavailableReason:
        'Excludes the Superseded/Severe cell, where 14 of 21 applications have no cost centre. Adding the known seven would understate the cell rather than complete it.',
    }),
    metric('MET-324', 'Median technical-debt score', 46, 'score', {
      desiredDirection: 'Down',
      confidence: 'Medium',
      qualityScore: 71,
      comparisonValue: 49,
      comparisonLabel: 'FY25 H2 close',
    }),
  ],
  lifecycles: [...LIFECYCLE_AXIS],
  debtBands: DEBT_AXIS,
  cells: CELLS,
  legend: [
    {
      disposition: 'Invest',
      label: 'Invest',
      shortCode: 'IN',
      description: 'Fit for purpose and strategically wanted. Fund new capability here.',
    },
    {
      disposition: 'Tolerate',
      label: 'Tolerate',
      shortCode: 'TO',
      description: 'Adequate for now. Keep running, spend nothing beyond support.',
    },
    {
      disposition: 'Migrate',
      label: 'Migrate',
      shortCode: 'MI',
      description: 'Still needed, wrong platform. Re-platform or replace with a successor.',
    },
    {
      disposition: 'Eliminate',
      label: 'Eliminate',
      shortCode: 'EL',
      description: 'No longer needed, or already superseded. Decommission and stop the spend.',
    },
  ],
  unplaced: [
    {
      applicationCount: 112,
      reason: 'No technical-debt assessment has been run, so the horizontal axis has no position.',
      remedy: 'Add them to the engineering scan scope; the next quarterly run would place them.',
    },
    {
      applicationCount: 46,
      reason: 'Lifecycle is not recorded in the CMDB, so the vertical axis has no position.',
      remedy:
        'Assign a lifecycle in the application inventory. Ownership sits with the domain lead.',
    },
  ],
  queue: QUEUE,
  modelVersion: 'disposition-model v4.0',
  assessedAt: '2026-05-04T01:00:00.000Z',
};

// ─────────────────────────────────────────────────────────────────────────────
// 3. Portfolio comparison (UC-03-013)
// ─────────────────────────────────────────────────────────────────────────────

const SNAPSHOTS: PortfolioSnapshotRef[] = [
  {
    id: 'fy24-h2',
    label: 'FY24 H2 close',
    asOf: '2024-12-31',
    taxonomyVersion: 'v2.4',
    productCount: 96,
    applicationCount: 1498,
    frozenAt: '2025-01-14T18:00:00.000Z',
  },
  {
    id: 'fy25-h2',
    label: 'FY25 H2 close',
    asOf: '2025-12-31',
    taxonomyVersion: 'v3.1',
    productCount: 138,
    applicationCount: 1684,
    frozenAt: '2026-01-16T18:00:00.000Z',
  },
  {
    id: 'fy26-h1',
    label: 'FY26 H1 (current)',
    asOf: AS_OF,
    taxonomyVersion: 'v4.0',
    productCount: 142,
    applicationCount: 1760,
    frozenAt: null,
  },
];

interface ChangeSeed {
  kind: PortfolioChangeKind;
  entityType: PortfolioChangeRow['entityType'];
  key: string;
  baselineName: string | null;
  comparisonName: string | null;
  baselineParent: string | null;
  comparisonParent: string | null;
  matchBasis: string;
  matchMethod: PortfolioChangeRow['matchMethod'];
  confidence: ConfidenceBand;
  counterparts?: string[];
  note?: string;
}

function change(seed: ChangeSeed): PortfolioChangeRow {
  return {
    id: `chg-${seed.key}`,
    kind: seed.kind,
    entityType: seed.entityType,
    subjectId: uuidFrom(`subject:${seed.key}`),
    baselineName: seed.baselineName,
    comparisonName: seed.comparisonName,
    baselineParent: seed.baselineParent,
    comparisonParent: seed.comparisonParent,
    matchBasis: seed.matchBasis,
    matchMethod: seed.matchMethod,
    confidence: seed.confidence,
    counterparts: seed.counterparts ?? [],
    note: seed.note ?? null,
  };
}

/**
 * The FY25 H2 → FY26 H1 change ledger.
 *
 * Every non-`Unchanged` row the server found, which is what lets the table's own
 * counts be checked against `changeCounts` rather than trusted. The convention
 * for merges and splits is stated on the screen: a merge is counted once per
 * baseline participant, a split once per baseline product, so the arithmetic
 * that reconciles 138 to 142 is reproducible by the reader.
 */
const CHANGES: PortfolioChangeRow[] = [
  change({
    kind: 'Added',
    entityType: 'Product',
    key: 'add-nfc-boarding',
    baselineName: null,
    comparisonName: 'Contactless Boarding',
    baselineParent: null,
    comparisonParent: 'Passenger Experience',
    matchBasis: 'No baseline counterpart; created 2026-02-02 with its own product id.',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Added',
    entityType: 'Product',
    key: 'add-saf-reporting',
    baselineName: null,
    comparisonName: 'SAF & Emissions Reporting',
    baselineParent: null,
    comparisonParent: 'Sustainability',
    matchBasis: 'No baseline counterpart; created 2026-01-19.',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Added',
    entityType: 'Product',
    key: 'add-crew-wellbeing',
    baselineName: null,
    comparisonName: 'Crew Wellbeing',
    baselineParent: null,
    comparisonParent: 'People',
    matchBasis: 'No baseline counterpart; created 2026-03-11.',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Added',
    entityType: 'Product',
    key: 'add-dynamic-pricing',
    baselineName: null,
    comparisonName: 'Dynamic Ancillary Pricing',
    baselineParent: null,
    comparisonParent: 'Commercial',
    matchBasis: 'No baseline counterpart; created 2026-02-24.',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Added',
    entityType: 'Product',
    key: 'add-cargo-quote',
    baselineName: null,
    comparisonName: 'Instant Cargo Quote',
    baselineParent: null,
    comparisonParent: 'Cargo',
    matchBasis: 'No baseline counterpart; created 2026-04-07.',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Added',
    entityType: 'Product',
    key: 'add-turnaround-intel',
    baselineName: null,
    comparisonName: 'Turnaround Intelligence',
    baselineParent: null,
    comparisonParent: 'Ground Operations',
    matchBasis: 'No baseline counterpart; created 2026-01-08.',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Added',
    entityType: 'Product',
    key: 'add-disruption-comms',
    baselineName: null,
    comparisonName: 'Disruption Communications',
    baselineParent: null,
    comparisonParent: 'Network Control',
    matchBasis:
      'No baseline counterpart found. It may have existed inside "Passenger Notifications" before the split — the crosswalk cannot tell.',
    matchMethod: 'AssistedCandidate',
    confidence: 'Low',
    note: 'Treated as an addition, but flagged: a reviewer should confirm it is not a rename.',
  }),
  change({
    kind: 'Added',
    entityType: 'Product',
    key: 'add-fleet-analytics',
    baselineName: null,
    comparisonName: 'Fleet Analytics',
    baselineParent: null,
    comparisonParent: 'Engineering & Maintenance',
    matchBasis: 'No baseline counterpart; created 2026-03-30.',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Removed',
    entityType: 'Product',
    key: 'rem-kiosk',
    baselineName: 'Airport Kiosk Suite',
    comparisonName: null,
    baselineParent: 'Passenger Experience',
    comparisonParent: null,
    matchBasis: 'Retired 2026-02-28 with a recorded decommission decision (DEC-2291).',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Removed',
    entityType: 'Product',
    key: 'rem-fax-gateway',
    baselineName: 'Partner Fax Gateway',
    comparisonName: null,
    baselineParent: 'Cargo',
    comparisonParent: null,
    matchBasis: 'Retired 2026-01-31 (DEC-2264).',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Removed',
    entityType: 'Product',
    key: 'rem-legacy-crew-pay',
    baselineName: 'Crew Pay (legacy)',
    comparisonName: null,
    baselineParent: 'People',
    comparisonParent: null,
    matchBasis: 'Superseded by the payroll platform; product closed 2026-03-15.',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Removed',
    entityType: 'Product',
    key: 'rem-microsite',
    baselineName: 'Campaign Microsites',
    comparisonName: null,
    baselineParent: 'Commercial',
    comparisonParent: null,
    matchBasis:
      'Absent from the comparison snapshot with no closure decision recorded. Absence is not the same as retirement.',
    matchMethod: 'RuleDerivedDeterministic',
    confidence: 'Low',
    note: 'No decommission record exists. This may be a data gap rather than a removal.',
  }),
  change({
    kind: 'Renamed',
    entityType: 'Product',
    key: 'ren-skymiles',
    baselineName: 'SkyMiles Programme',
    comparisonName: 'Aurora Rewards',
    baselineParent: 'Loyalty & Retail',
    comparisonParent: 'Loyalty & Retail',
    matchBasis: 'Same product id on both sides. The name changed; the product did not.',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Renamed',
    entityType: 'Product',
    key: 'ren-irregops',
    baselineName: 'IrregOps',
    comparisonName: 'Disruption Recovery',
    baselineParent: 'Network Control',
    comparisonParent: 'Network Control',
    matchBasis: 'Same product id on both sides.',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Renamed',
    entityType: 'Product',
    key: 'ren-bagops',
    baselineName: 'Baggage Ops',
    comparisonName: 'Baggage Journey',
    baselineParent: 'Ground Operations',
    comparisonParent: 'Ground Operations',
    matchBasis: 'Same product id on both sides.',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Renamed',
    entityType: 'Product',
    key: 'ren-selfservice',
    baselineName: 'Self Service',
    comparisonName: 'Digital Self Service',
    baselineParent: 'Passenger Experience',
    comparisonParent: 'Passenger Experience',
    matchBasis:
      'Product ids differ. Matched on 94% name similarity and an identical application set (11 of 11).',
    matchMethod: 'AssistedCandidate',
    confidence: 'Medium',
    note: 'Matched by heuristic, not by id. If this is wrong it is one addition and one removal.',
  }),
  change({
    kind: 'Moved',
    entityType: 'Product',
    key: 'mov-fuel',
    baselineName: 'Fuel Efficiency',
    comparisonName: 'Fuel Efficiency',
    baselineParent: 'Flight Operations',
    comparisonParent: 'Sustainability',
    matchBasis: 'Same product id; parent changed under taxonomy v4.0.',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Moved',
    entityType: 'Product',
    key: 'mov-crewapp',
    baselineName: 'Crew Companion',
    comparisonName: 'Crew Companion',
    baselineParent: 'Flight Operations',
    comparisonParent: 'People',
    matchBasis: 'Same product id; parent changed under taxonomy v4.0.',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Moved',
    entityType: 'Product',
    key: 'mov-lounge',
    baselineName: 'Lounge Experience',
    comparisonName: 'Lounge Experience',
    baselineParent: 'Passenger Experience',
    comparisonParent: 'Loyalty & Retail',
    matchBasis: 'Same product id; parent changed 2026-02-09.',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Moved',
    entityType: 'Product',
    key: 'mov-cargo-track',
    baselineName: 'Cargo Tracking',
    comparisonName: 'Cargo Tracking',
    baselineParent: 'Cargo',
    comparisonParent: 'Ground Operations',
    matchBasis: 'Same product id; parent changed under taxonomy v4.0.',
    matchMethod: 'SystemDerivedExact',
    confidence: 'High',
  }),
  change({
    kind: 'Moved',
    entityType: 'Product',
    key: 'mov-identity',
    baselineName: 'Passenger Identity',
    comparisonName: 'Passenger Identity',
    baselineParent: 'Passenger Experience',
    comparisonParent: 'Security & Compliance',
    matchBasis:
      'Same product id. The parent value stream did not exist in v3.1, so the move is a consequence of the taxonomy change rather than a decision.',
    matchMethod: 'RuleDerivedDeterministic',
    confidence: 'Medium',
    note: 'Counted as a move, but nothing about this product changed — only the tree around it.',
  }),
  change({
    kind: 'Merged',
    entityType: 'Product',
    key: 'mrg-notify-a',
    baselineName: 'Passenger Notifications',
    comparisonName: 'Passenger Messaging',
    baselineParent: 'Passenger Experience',
    comparisonParent: 'Passenger Experience',
    matchBasis: 'Steward-recorded merge (MRG-0142), both baseline products into one.',
    matchMethod: 'GovernanceApproved',
    confidence: 'High',
    counterparts: ['Inflight Messaging'],
  }),
  change({
    kind: 'Merged',
    entityType: 'Product',
    key: 'mrg-notify-b',
    baselineName: 'Inflight Messaging',
    comparisonName: 'Passenger Messaging',
    baselineParent: 'Passenger Experience',
    comparisonParent: 'Passenger Experience',
    matchBasis: 'Steward-recorded merge (MRG-0142).',
    matchMethod: 'GovernanceApproved',
    confidence: 'High',
    counterparts: ['Passenger Notifications'],
  }),
  change({
    kind: 'Split',
    entityType: 'Product',
    key: 'spl-ops-control',
    baselineName: 'Operations Control',
    comparisonName: 'Network Operations Control',
    baselineParent: 'Network Control',
    comparisonParent: 'Network Control',
    matchBasis: 'Steward-recorded split (SPL-0037) into two products.',
    matchMethod: 'GovernanceApproved',
    confidence: 'High',
    counterparts: ['Crew Operations Control'],
    note: 'Counted once, on the baseline product. It contributes two products to the comparison side.',
  }),
];

const METRIC_DELTAS: PortfolioMetricDelta[] = [
  {
    metricId: 'MET-001',
    label: 'Products in portfolio',
    baseline: metric('MET-001', 'Products in portfolio', 138, 'count', {
      periodStart: '2025-07-01',
      periodEnd: '2025-12-31',
      confidence: 'High',
      qualityScore: 92,
    }),
    comparison: metric('MET-001', 'Products in portfolio', 142, 'count', {
      confidence: 'High',
      qualityScore: 92,
    }),
    deltaAbsolute: 4,
    deltaPercent: 0.029,
    comparable: true,
    incomparableReason: null,
  },
  {
    metricId: 'MET-002',
    label: 'Applications in estate',
    baseline: metric('MET-002', 'Applications in estate', 1684, 'count', {
      periodStart: '2025-07-01',
      periodEnd: '2025-12-31',
      confidence: 'High',
      qualityScore: 88,
    }),
    comparison: metric('MET-002', 'Applications in estate', 1760, 'count', {
      confidence: 'High',
      qualityScore: 88,
    }),
    deltaAbsolute: 76,
    deltaPercent: 0.045,
    comparable: true,
    incomparableReason: null,
  },
  {
    metricId: 'MET-014',
    label: 'Annual run cost',
    baseline: metric('MET-014', 'Annual run cost', money(148_200_000), 'currency', {
      periodStart: '2025-07-01',
      periodEnd: '2025-12-31',
      confidence: 'Medium',
      qualityScore: 74,
    }),
    comparison: metric('MET-014', 'Annual run cost', money(154_900_000), 'currency', {
      confidence: 'Medium',
      qualityScore: 74,
    }),
    deltaAbsolute: 6_700_000,
    deltaPercent: 0.045,
    comparable: true,
    incomparableReason: null,
  },
  {
    metricId: 'MET-037',
    label: 'Applications per product',
    baseline: metric('MET-037', 'Applications per product', 12.2, 'ratio', {
      periodStart: '2025-07-01',
      periodEnd: '2025-12-31',
      definitionVersion: 'v3.1',
      confidence: 'Medium',
      qualityScore: 70,
    }),
    comparison: metric('MET-037', 'Applications per product', 12.4, 'ratio', {
      definitionVersion: 'v4.0',
      confidence: 'Medium',
      qualityScore: 70,
    }),
    deltaAbsolute: null,
    deltaPercent: null,
    comparable: false,
    incomparableReason:
      'The definition changed between v3.1 and v4.0: shared platform applications now count against every product that uses them, where before they counted once. The two figures measure different things and their difference means nothing.',
  },
  {
    metricId: 'MET-052',
    label: 'Products with a named owner',
    baseline: metric('MET-052', 'Products with a named owner', null, 'percent', {
      status: 'Unavailable',
      periodStart: '2025-07-01',
      periodEnd: '2025-12-31',
      confidence: 'Unknown',
      qualityScore: null,
      unavailableReason:
        'Ownership was not captured as a governed field until March 2026, so the FY25 H2 snapshot has nothing to report.',
    }),
    comparison: metric('MET-052', 'Products with a named owner', 0.87, 'percent', {
      desiredDirection: 'Up',
      confidence: 'High',
      qualityScore: 86,
    }),
    deltaAbsolute: null,
    deltaPercent: null,
    comparable: false,
    incomparableReason:
      'The baseline side has no value: ownership was not a governed field in FY25 H2. 87% is not an improvement on nothing.',
  },
];

const PARTIAL_VERDICT: ComparabilityVerdict = {
  level: 'Partial',
  statement:
    'These two snapshots can be compared at product level, but the value-stream layer was rebuilt in taxonomy v4.0, so any change involving a parent has to be read as a possible consequence of the rebuild rather than a decision.',
  taxonomyChanged: true,
  caveats: [
    {
      area: 'Value-stream parents',
      reason:
        'v4.0 introduced Sustainability and Security & Compliance. Products that appear to have moved into them did not necessarily move — the destination simply did not exist before.',
    },
    {
      area: 'Applications per product (MET-037)',
      reason:
        'The definition changed with the taxonomy. The two sides are not measuring the same thing.',
    },
    {
      area: 'Product ownership',
      reason: 'Not a governed field before March 2026; the baseline side is empty, not zero.',
    },
  ],
};

function noCrosswalkVerdict(baseline: PortfolioSnapshotRef, comparison: PortfolioSnapshotRef) {
  const verdict: ComparabilityVerdict = {
    level: 'None',
    statement: `${baseline.label} (taxonomy ${baseline.taxonomyVersion}) and ${comparison.label} (taxonomy ${comparison.taxonomyVersion}) cannot be compared: the 2025 taxonomy rebuild replaced the product identifiers, and no crosswalk between the two id sets was retained.`,
    taxonomyChanged: true,
    caveats: [
      {
        area: 'Product identity',
        reason:
          'Product ids were reissued in the rebuild. Matching by name alone would produce a change ledger whose every row is a guess, so none is offered.',
      },
      {
        area: 'All metrics',
        reason:
          'Every metric on these two snapshots is scoped by a hierarchy that does not survive the rebuild.',
      },
    ],
  };
  return verdict;
}

// ── The repository surface ───────────────────────────────────────────────────

export interface EstateRepository {
  getDuplicateReview(): Promise<QueryResult<DuplicateReviewView>>;
  getTechnologyHeatmap(): Promise<QueryResult<TechnologyHeatmapView>>;
  /** Both ids come from `listPortfolioSnapshots()`. Unknown ids reject. */
  comparePortfolio(
    baselineId?: string,
    comparisonId?: string,
  ): Promise<QueryResult<PortfolioComparisonView>>;
}

export function listPortfolioSnapshots(): PortfolioSnapshotRef[] {
  return SNAPSHOTS;
}

export const DEFAULT_COMPARISON = { baselineId: 'fy25-h2', comparisonId: 'fy26-h1' } as const;

/**
 * The estate repository over the fixtures above.
 *
 * Async on purpose, matching the other repositories: the screens must already
 * be written against a promise, or swapping in the HTTP client later becomes a
 * rewrite of every component that consumes one of these.
 */
export function createEstateRepository(): EstateRepository {
  return {
    async getDuplicateReview(_filters?: unknown) {
      return {
        data: DUPLICATE_VIEW,
        meta: estateMeta({
          correlationId: 'estate-duplicates-0001',
          sources: [
            { name: 'AURORA-CMDB', status: 'Available', recordCount: 1760 },
            { name: 'Process Repository', status: 'Partial', recordCount: 1412 },
            { name: 'Contract Register', status: 'Available', recordCount: 412 },
          ],
          qualityScore: 71,
          openIssueCount: 6,
          confidence: 'Medium',
          allowedActions: [
            {
              action: 'duplicates.dismiss',
              permission: 'edit',
              enabled: true,
              disabledReason: null,
            },
            {
              action: 'duplicates.export',
              permission: 'export',
              enabled: true,
              disabledReason: null,
            },
            {
              action: 'duplicates.confirm',
              permission: 'approve',
              enabled: false,
              disabledReason:
                'Confirming an overlap raises a consolidation decision, which needs the Portfolio Steward role.',
            },
          ],
        }),
      };
    },

    async getTechnologyHeatmap() {
      return {
        data: HEATMAP_VIEW,
        meta: estateMeta({
          correlationId: 'estate-heatmap-0001',
          sources: [
            { name: 'AURORA-CMDB', status: 'Available', recordCount: 1760 },
            { name: 'Engineering Debt Scan', status: 'Partial', recordCount: 1648 },
            { name: 'Finance ERP', status: 'Available', recordCount: 1591 },
          ],
          qualityScore: 76,
          openIssueCount: 4,
          confidence: 'Medium',
          allowedActions: [
            { action: 'heatmap.export', permission: 'export', enabled: true, disabledReason: null },
            {
              action: 'heatmap.setDisposition',
              permission: 'propose',
              enabled: false,
              disabledReason:
                'A disposition is proposed against an application, not against a cell. Open an application to set one.',
            },
          ],
        }),
      };
    },

    async comparePortfolio(baselineId?: string, comparisonId?: string) {
      // Absent means "you choose", which the fixture answers with the last two
      // snapshots it holds — the same contract the HTTP repository honours.
      const fallbackBaseline = SNAPSHOTS[SNAPSHOTS.length - 2] ?? SNAPSHOTS[0];
      const fallbackComparison = SNAPSHOTS[SNAPSHOTS.length - 1];
      const baseline = baselineId
        ? SNAPSHOTS.find((snapshot) => snapshot.id === baselineId)
        : fallbackBaseline;
      const comparison = comparisonId
        ? SNAPSHOTS.find((snapshot) => snapshot.id === comparisonId)
        : fallbackComparison;
      if (!baseline || !comparison) {
        throw new Error(
          `Unknown snapshot: ${!baseline ? baselineId : comparisonId}. Choose from ${SNAPSHOTS.map((snapshot) => snapshot.id).join(', ')}.`,
        );
      }

      // Only the FY25 H2 → FY26 H1 pair survived the taxonomy rebuild with a
      // crosswalk. Everything else returns an honest refusal rather than a
      // ledger of name-matched guesses.
      const crosswalked = baseline.id === 'fy25-h2' && comparison.id === 'fy26-h1';

      const data: PortfolioComparisonView = {
        baseline,
        comparison,
        availableSnapshots: SNAPSHOTS,
        comparability: crosswalked ? PARTIAL_VERDICT : noCrosswalkVerdict(baseline, comparison),
        changeCounts: crosswalked
          ? [
              { kind: 'Added', count: 8 },
              { kind: 'Removed', count: 4 },
              { kind: 'Renamed', count: 4 },
              { kind: 'Moved', count: 5 },
              { kind: 'Merged', count: 2 },
              { kind: 'Split', count: 1 },
              { kind: 'Unchanged', count: 122 },
            ]
          : [],
        changes: crosswalked ? CHANGES : [],
        metricDeltas: crosswalked ? METRIC_DELTAS : [],
        unmatched: crosswalked
          ? [
              {
                side: 'baseline',
                count: 3,
                reason:
                  'Present in FY25 H2 with no id and no name close enough to match. Left unmatched rather than reported as removed.',
              },
              {
                side: 'comparison',
                count: 2,
                reason:
                  'Created between the two dates and retired again before FY26 H1 closed, so neither side holds a full record.',
              },
            ]
          : [],
      };

      return {
        data,
        meta: estateMeta({
          correlationId: `estate-compare-${baseline.id}-${comparison.id}`,
          sources: [
            { name: 'Portfolio Snapshots', status: 'Available', recordCount: 3 },
            { name: 'AURORA-CMDB', status: 'Available', recordCount: 1760 },
            { name: 'Finance ERP', status: 'Available', recordCount: 1591 },
          ],
          qualityScore: crosswalked ? 79 : 40,
          openIssueCount: crosswalked ? 3 : 1,
          confidence: crosswalked ? 'Medium' : 'Low',
          allowedActions: [
            { action: 'compare.export', permission: 'export', enabled: true, disabledReason: null },
          ],
        }),
      };
    },
  };
}
